// =============================================================================
// ACT ENGLISH TEST BANK - Form 25MC4
// Source: The Official ACT Prep Guide 2025-2026 (Practice Test 4)
// Total Questions: 50 (40 scored, 10 field test)
// Categories: POW (Production of Writing), KLA (Knowledge of Language), CSE (Conventions of Standard English)
// =============================================================================

export const form25MC4 = {
  id: 'english-25MC4',
  formCode: '25MC4',
  subject: 'english',
  year: 2025,
  source: 'Official ACT Prep Guide 2025-2026',
  totalQuestions: 50,
  scoredQuestions: 40,
  timeLimit: 35, // Standard time for scored questions
  standardTime: 45, // Full test time
  fieldTestPositions: [41, 42, 43, 44, 45, 46, 47, 48, 49, 50],
  passages: [
    {
      id: 'passage-1',
      number: 1,
      title: 'A Mouthful of Music',
      questionRange: [1, 5],
      description: 'Essay about mouth music, a form of vocal music that imitates instruments, particularly popular in Celtic cultures'
    },
    {
      id: 'passage-2',
      number: 2,
      title: 'Making the Desert Bloom',
      questionRange: [6, 15],
      description: 'Essay about the Garamantes, an ancient civilization that created underground water tunnels (foggaras) in the Sahara Desert'
    },
    {
      id: 'passage-3',
      number: 3,
      title: 'Faith Ringgold\'s Quilting Bee',
      questionRange: [16, 25],
      description: 'Essay about artist Faith Ringgold and her "story quilts" combining painting, quilting, and storytelling'
    },
    {
      id: 'passage-4',
      number: 4,
      title: 'Neutrinos on Ice',
      questionRange: [26, 35],
      description: 'Essay about the IceCube Neutrino Observatory in Antarctica and the study of neutrinos'
    },
    {
      id: 'passage-5',
      number: 5,
      title: 'Clinton Hill\'s Found Artist',
      questionRange: [36, 45],
      description: 'Personal essay about discovering artist Rafael Leonardo Black through a New York Times article'
    },
    {
      id: 'passage-6',
      number: 6,
      title: 'Cher Ami, Pigeon Hero',
      questionRange: [46, 50],
      description: 'Essay about Cher Ami, a famous homing pigeon that saved hundreds of lives during World War I'
    }
  ],
  questions: [
    // PASSAGE I: A Mouthful of Music (Q1-5)
    {
      id: 'english-25MC4-1',
      formId: 'english-25MC4',
      questionNumber: 1,
      passageId: 'passage-1',
      category: 'CSE',
      isScored: true,
      question: 'Mouth music is the name for the many ways of imitating the sounds of musical instruments with the human voice. Forms of mouth music are performed around the world, but the genre being particularly popular in England, Ireland, and Scotland.',
      underlinedText: 'being particularly',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'was being' },
        { letter: 'C', text: 'is' },
        { letter: 'D', text: 'Delete the underlined portion.' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: 'The sentence needs a main verb for the independent clause. "Is" creates a proper parallel structure with the first independent clause and maintains present tense consistency.'
    },
    {
      id: 'english-25MC4-2',
      formId: 'english-25MC4',
      questionNumber: 2,
      passageId: 'passage-1',
      category: 'CSE',
      isScored: true,
      question: 'Celtic mouth music exists to accompany dancing. Instead of using traditional lyrics, singers often produce nonsense syllables, called vocables to represent specific instrumental sounds, such as those of bagpipes or violins.',
      underlinedText: 'syllables, called vocables',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'syllables called vocables,' },
        { letter: 'H', text: 'syllables, called vocables,' },
        { letter: 'J', text: 'syllables called, vocables,' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: 'The phrase "called vocables" is a nonessential appositive that should be set off by commas on both sides.'
    },
    {
      id: 'english-25MC4-3',
      formId: 'english-25MC4',
      questionNumber: 3,
      passageId: 'passage-1',
      category: 'POW',
      isScored: true,
      question: 'The often tongue-twisting lyrics require much practice to perfect.',
      underlinedText: 'tongue-twisting',
      questionPrompt: 'If the writer were to delete the underlined portion, the sentence would primarily lose:',
      choices: [
        { letter: 'A', text: 'a description that emphasizes the difficulty of puirt-a-beul.' },
        { letter: 'B', text: 'information about writing lyrics for puirt-a-beul.' },
        { letter: 'C', text: 'an indication of how often puirt-a-beul is performed in Celtic culture.' },
        { letter: 'D', text: 'an example of a training exercise puirt-a-beul singers use to practice lyrics.' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: '"Tongue-twisting" describes the difficulty of pronouncing the lyrics, emphasizing that puirt-a-beul is challenging to perform.'
    },
    {
      id: 'english-25MC4-4',
      formId: 'english-25MC4',
      questionNumber: 4,
      passageId: 'passage-1',
      category: 'POW',
      isScored: true,
      question: '[4] Instruments were prohibitively expensive and thus scarce in isolated Scottish villages. In order to fill the void, mouth music emerged and provided residents with the music they wanted for dancing.',
      questionPrompt: 'Given that all the following statements are true, which one, if added here, would most effectively introduce the subject of the paragraph?',
      choices: [
        { letter: 'F', text: 'Puirt-a-beul was most likely invented out of necessity.' },
        { letter: 'G', text: 'Mouth music singers must have a good sense of rhythm.' },
        { letter: 'H', text: 'Celtic mouth music, including puirt-a-beul, has influenced jazz scat singing.' },
        { letter: 'J', text: 'Another form of mouth music that originated in Scotland is the waulking song.' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: 'The paragraph discusses how puirt-a-beul arose because instruments were scarce—it was invented out of necessity to provide music for dancing.'
    },
    {
      id: 'english-25MC4-5',
      formId: 'english-25MC4',
      questionNumber: 5,
      passageId: 'passage-1',
      category: 'CSE',
      isScored: true,
      question: 'Additionally, puirt-a-beul gave anyone whomever didn\'t read music a way to learn and pass on traditional songs.',
      underlinedText: 'whomever',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'which' },
        { letter: 'C', text: 'whom' },
        { letter: 'D', text: 'who' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: '"Who" is correct because it is the subject of the verb "didn\'t read." The pronoun refers to "anyone" and functions as the subject of the relative clause.'
    },

    // PASSAGE II: Making the Desert Bloom (Q6-15)
    {
      id: 'english-25MC4-6',
      formId: 'english-25MC4',
      questionNumber: 6,
      passageId: 'passage-2',
      category: 'CSE',
      isScored: true,
      question: 'More than two thousand years ago, a people the Romans, called the Garamantes, created a complex civilization in one of the world\'s driest places—the Sahara Desert.',
      underlinedText: 'Romans, called the Garamantes,',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'Romans called the Garamantes,' },
        { letter: 'H', text: 'Romans called: the Garamantes' },
        { letter: 'J', text: 'Romans called the Garamantes' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: 'No commas are needed. "Called the Garamantes" is a restrictive element that identifies which people, so it should not be set off by commas.'
    },
    {
      id: 'english-25MC4-7',
      formId: 'english-25MC4',
      questionNumber: 7,
      passageId: 'passage-2',
      category: 'KLA',
      isScored: true,
      question: 'Beginning around 500 BCE, they built towns and villages, cloth was manufactured there and jewelry, and traded throughout North Africa and the Mediterranean.',
      underlinedText: 'cloth was manufactured there',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'the manufacture of cloth took place' },
        { letter: 'C', text: 'manufactured cloth' },
        { letter: 'D', text: 'cloth' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: '"Manufactured cloth" maintains parallel structure with the other items in the series: "built towns and villages, manufactured cloth and jewelry, and traded..."'
    },
    {
      id: 'english-25MC4-8',
      formId: 'english-25MC4',
      questionNumber: 8,
      passageId: 'passage-2',
      category: 'KLA',
      isScored: true,
      question: 'The water ran through sloping, hand-dug tunnels called foggaras, which could be as deep as one hundred thirty feet below ground.',
      underlinedText: 'hand-dug',
      questionPrompt: 'Which choice best indicates the method used to build the tunnels?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'underground' },
        { letter: 'H', text: 'dimly lit' },
        { letter: 'J', text: 'desert' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: '"Hand-dug" specifically indicates the method of construction—the tunnels were dug by hand. The other options describe characteristics but not the building method.'
    },
    {
      id: 'english-25MC4-9',
      formId: 'english-25MC4',
      questionNumber: 9,
      passageId: 'passage-2',
      category: 'CSE',
      isScored: true,
      question: 'Having left no clues, archaeologists don\'t know how the Garamantes learned to build foggaras.',
      underlinedText: 'Having left no clues, archaeologists',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'A genuine puzzle to scientists, archaeologists' },
        { letter: 'C', text: 'Giving no indication, archaeologists' },
        { letter: 'D', text: 'Archaeologists' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: 'The original creates a dangling modifier—"Having left no clues" incorrectly modifies "archaeologists." Simply starting with "Archaeologists" eliminates the error.'
    },
    {
      id: 'english-25MC4-10',
      formId: 'english-25MC4',
      questionNumber: 10,
      passageId: 'passage-2',
      category: 'CSE',
      isScored: true,
      question: 'And because the water came from an aquifer rather then from its rainfall, the supply was unaffected by drought.',
      underlinedText: 'then from its',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'than from' },
        { letter: 'H', text: 'then' },
        { letter: 'J', text: 'by' }
      ],
      correctIndex: 1,
      correctLetter: 'G',
      explanation: '"Than" is used for comparisons ("rather than"), and "from" is needed to maintain parallel structure with "from an aquifer."'
    },
    {
      id: 'english-25MC4-11',
      formId: 'english-25MC4',
      questionNumber: 11,
      passageId: 'passage-2',
      category: 'CSE',
      isScored: true,
      question: 'The Garamantes could of relied on a constant supply of water for drinking, washing, and irrigation.',
      underlinedText: 'could of relied',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'had to of relied' },
        { letter: 'C', text: 'could rely' },
        { letter: 'D', text: 'relies' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: '"Could of" is a common error for "could have." However, "could rely" is the best choice here as it maintains the modal verb construction and proper tense.'
    },
    {
      id: 'english-25MC4-12',
      formId: 'english-25MC4',
      questionNumber: 12,
      passageId: 'passage-2',
      category: 'POW',
      isScored: true,
      question: 'Moreover, the cold, damp air of the foggaras lowered the temperature inside the homes that were built over them, resulting in an ancient form of air-conditioning.',
      underlinedText: 'Moreover,',
      questionPrompt: 'Which transition word or phrase is most logical in context?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'Nevertheless,' },
        { letter: 'H', text: 'In contrast,' },
        { letter: 'J', text: 'Even so,' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: '"Moreover" correctly indicates that this sentence adds another benefit of the foggaras, building on the previous discussion of water supply.'
    },
    {
      id: 'english-25MC4-13',
      formId: 'english-25MC4',
      questionNumber: 13,
      passageId: 'passage-2',
      category: 'POW',
      isScored: true,
      question: 'The Garamantes thrived until about 500 CE, when some archaeologists believe they began to deplete the aquifer. As the foggaras supplied less and less water, the Garamantes\' population declined.',
      underlinedText: 'As',
      questionPrompt: 'Which choice is clearest and most precise in context?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'As to when' },
        { letter: 'C', text: 'Whereas' },
        { letter: 'D', text: 'Though' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: '"As" correctly establishes a cause-and-effect relationship: as water decreased, the population declined.'
    },
    {
      id: 'english-25MC4-14',
      formId: 'english-25MC4',
      questionNumber: 14,
      passageId: 'passage-2',
      category: 'KLA',
      isScored: true,
      question: 'The stone mounds that mark their ventilation shafts are still visible in what is now southwestern Libya, where they can be seen even now.',
      underlinedText: 'where they can be seen even now',
      questionPrompt: 'Which choice is least redundant in context?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'a place where visitors can see these amazing signs of an ancient civilization.' },
        { letter: 'H', text: 'the location that continues to present visitors with a view of these remnants of a time gone by.' },
        { letter: 'J', text: 'Delete the underlined portion and end the sentence with a period.' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: 'The underlined portion is redundant—"still visible" already conveys that they can be seen now. Deleting it eliminates the redundancy.'
    },
    {
      id: 'english-25MC4-15',
      formId: 'english-25MC4',
      questionNumber: 15,
      passageId: 'passage-2',
      category: 'POW',
      isScored: true,
      question: 'Question 15 asks about the preceding passage as a whole.',
      questionPrompt: 'Suppose the writer\'s primary purpose had been to present information about a civilization\'s efforts to overcome a natural obstacle in order to survive. Would this essay accomplish that purpose?',
      choices: [
        { letter: 'A', text: 'Yes, because it explains that the Garamantes traded throughout North Africa and the Mediterranean.' },
        { letter: 'B', text: 'Yes, because it describes the Garamantes\' method of bringing water to an otherwise dry area, allowing the Garamantes to thrive there.' },
        { letter: 'C', text: 'No, because the foggaras were not naturally occurring tunnels.' },
        { letter: 'D', text: 'No, because the foggaras ultimately led to the downfall of the Garamantes\' civilization.' }
      ],
      correctIndex: 1,
      correctLetter: 'B',
      explanation: 'The essay focuses on how the Garamantes overcame the natural obstacle of living in the desert by creating an ingenious water system (foggaras) that allowed their civilization to thrive.'
    },

    // PASSAGE III: Faith Ringgold's Quilting Bee (Q16-25)
    {
      id: 'english-25MC4-16',
      formId: 'english-25MC4',
      questionNumber: 16,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: 'One of these artworks, The Sunflowers Quilting Bee at Arles, depicts a scene of women at work on a quilt in a field of towering yellow flowers that eight African American women sit around the quilt that covers their laps.',
      underlinedText: 'flowers that eight',
      questionPrompt: 'Which choice makes the sentence or sentences most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'flowers and eight' },
        { letter: 'H', text: 'flowers. Eight' },
        { letter: 'J', text: 'flowers, eight' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: 'The original creates a run-on sentence. A period after "flowers" creates two complete, grammatically correct sentences.'
    },
    {
      id: 'english-25MC4-17',
      formId: 'english-25MC4',
      questionNumber: 17,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: 'Thus, the answers to these questions can be found in the artwork itself.',
      underlinedText: 'Thus, the',
      questionPrompt: 'Which transition word, if any, is most logical in context?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'Instead, the' },
        { letter: 'C', text: 'Furthermore, the' },
        { letter: 'D', text: 'The' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: 'No transition is needed here. The sentence directly answers the questions posed in the previous sentence without requiring a logical connector.'
    },
    {
      id: 'english-25MC4-18',
      formId: 'english-25MC4',
      questionNumber: 18,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: 'One panel is sewn into the piece\'s top border, the other into it\'s bottom border.',
      underlinedText: 'it\'s',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'its\'' },
        { letter: 'H', text: 'its' },
        { letter: 'J', text: 'their' }
      ],
      correctIndex: 2,
      correctLetter: 'H',
      explanation: '"Its" (possessive, no apostrophe) is correct. "It\'s" means "it is," which doesn\'t make sense here.'
    },
    {
      id: 'english-25MC4-19',
      formId: 'english-25MC4',
      questionNumber: 19,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: 'In reality, these women never met to piece together a quilt.',
      underlinedText: 'In reality,',
      questionPrompt: 'Which transition word is most logical in context?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'summary,' },
        { letter: 'C', text: 'addition,' },
        { letter: 'D', text: 'contrast,' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: '"In reality" correctly signals that the truth (they never actually met) contrasts with what the artwork depicts.'
    },
    {
      id: 'english-25MC4-20',
      formId: 'english-25MC4',
      questionNumber: 20,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: 'The scene comes out of the artists imagination as a statement of the unity of purpose that she perceives in their lives.',
      underlinedText: 'artists imagination',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'artist\'s imagination' },
        { letter: 'H', text: 'artists\' imagination' },
        { letter: 'J', text: 'artists imagination,' }
      ],
      correctIndex: 1,
      correctLetter: 'G',
      explanation: 'The singular possessive "artist\'s" is needed because the imagination belongs to one artist (Ringgold).'
    },
    {
      id: 'english-25MC4-21',
      formId: 'english-25MC4',
      questionNumber: 21,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: 'Newspaper journalist Ida B. Wells courageously spoke out for social and racial justice in the late nineteenth and early twentieth centuries.',
      underlinedText: 'courageously spoke out for social and racial justice',
      questionPrompt: 'Given that all the choices are true, which one provides the most relevant information at this point in the essay?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'married Ferdinand Barnett, editor of the first Black newspaper in Chicago, the Chicago Conservator.' },
        { letter: 'C', text: 'wrote for newspapers in Memphis, New York City, and finally, Chicago.' },
        { letter: 'D', text: 'was born in Holly Springs, Mississippi, in 1862, the eldest of eight children.' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: 'The essay focuses on the shared commitment to social justice among the women depicted. Wells\'s advocacy for racial justice is most relevant to this theme.'
    },
    {
      id: 'english-25MC4-22',
      formId: 'english-25MC4',
      questionNumber: 22,
      passageId: 'passage-3',
      category: 'KLA',
      isScored: true,
      question: 'Establishing her own hair products business herself in the first decade of the twentieth century, Madam C. J. Walker later bequeathed millions of dollars to charities and educational institutions.',
      underlinedText: 'business herself',
      questionPrompt: 'Which choice is least redundant in context?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'business belonging to her' },
        { letter: 'H', text: 'business, herself,' },
        { letter: 'J', text: 'business' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: '"Her own" already indicates personal ownership, making "herself" redundant. Simply "business" eliminates the redundancy.'
    },
    {
      id: 'english-25MC4-23',
      formId: 'english-25MC4',
      questionNumber: 23,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: 'Among the schools that benefited from this generosity, were those that Mary McLeod Bethune opened and ran in order to provide a better education for Black students.',
      underlinedText: 'generosity, were',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'generosity; were' },
        { letter: 'C', text: 'generosity were' },
        { letter: 'D', text: 'generosity were:' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: 'No punctuation should separate the subject ("schools") from the verb ("were"). The comma after "generosity" incorrectly breaks up the sentence.'
    },
    {
      id: 'english-25MC4-24',
      formId: 'english-25MC4',
      questionNumber: 24,
      passageId: 'passage-3',
      category: 'CSE',
      isScored: true,
      question: 'And Fannie Lou Hamer, Ella Baker, and Rosa Parks showed leadership and strength during the civil rights movement, it happened in the 1950s and 1960s.',
      underlinedText: 'movement, it happened',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'movement, it took place in' },
        { letter: 'H', text: 'movement, that happened in' },
        { letter: 'J', text: 'movement of' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: '"Of the 1950s and 1960s" creates a proper prepositional phrase that modifies "civil rights movement" without creating a run-on sentence.'
    },
    {
      id: 'english-25MC4-25',
      formId: 'english-25MC4',
      questionNumber: 25,
      passageId: 'passage-3',
      category: 'POW',
      isScored: true,
      question: 'In the artwork, Ringgold has surrounded these women with bright sunflowers. The flowers seem to celebrate the women\'s accomplishments and the beauty of their shared vision. [25]',
      questionPrompt: 'If the writer were to delete the preceding sentence, the essay would primarily lose:',
      choices: [
        { letter: 'A', text: 'an interpretation of the artwork that serves to summarize the essay.' },
        { letter: 'B', text: 'a reflection on the women depicted in the artwork that compares them to Ringgold.' },
        { letter: 'C', text: 'a description of a brushwork technique that refers back to the essay\'s opening.' },
        { letter: 'D', text: 'an evaluation of Ringgold\'s artistic talent that places her in a historical context.' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: 'The sentence interprets the symbolism of the sunflowers (celebration of accomplishments and shared vision), which ties together the essay\'s discussion of the women\'s contributions.'
    },

    // PASSAGE IV: Neutrinos on Ice (Q26-35)
    {
      id: 'english-25MC4-26',
      formId: 'english-25MC4',
      questionNumber: 26,
      passageId: 'passage-4',
      category: 'CSE',
      isScored: true,
      question: 'Each cable is equipped with sixty digital optical modules (DOMs), which, are programmed, to detect a faint blue flash known as Cherenkov radiation.',
      underlinedText: '(DOMs), which, are programmed,',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: '(DOMs), which are programmed' },
        { letter: 'H', text: '(DOMs): which are programmed' },
        { letter: 'J', text: '(DOMs); which are programmed' }
      ],
      correctIndex: 1,
      correctLetter: 'G',
      explanation: 'The commas around "which" and after "programmed" incorrectly interrupt the flow. A single comma after the closing parenthesis, followed by the relative clause, is correct.'
    },
    {
      id: 'english-25MC4-27',
      formId: 'english-25MC4',
      questionNumber: 27,
      passageId: 'passage-4',
      category: 'POW',
      isScored: true,
      question: 'Although there are countless neutrinos in the universe (fifty trillion neutrinos pass through your body every second), actually detecting them is a formidable task.',
      underlinedText: '(fifty trillion neutrinos pass through your body every second), actually detecting them is a formidable',
      questionPrompt: 'If the writer were to delete the underlined portion (adjusting the punctuation as needed), the essay would primarily lose information that:',
      choices: [
        { letter: 'A', text: 'specifies why neutrinos are practically weightless.' },
        { letter: 'B', text: 'explains how neutrinos pass through matter.' },
        { letter: 'C', text: 'indicates why there are so many neutrinos.' },
        { letter: 'D', text: 'emphasizes how numerous neutrinos are.' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: 'The parenthetical information (fifty trillion neutrinos per second) emphasizes the enormous number of neutrinos that exist, reinforcing "countless."'
    },
    {
      id: 'english-25MC4-28',
      formId: 'english-25MC4',
      questionNumber: 28,
      passageId: 'passage-4',
      category: 'POW',
      isScored: true,
      question: 'Neutrinos are rarely affected by matter or electromagnetic fields. For this purpose, many neutrinos have been traveling through space unimpeded for billions of years.',
      underlinedText: 'For this purpose,',
      questionPrompt: 'Which transition word or phrase is most logical in context?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'In contrast,' },
        { letter: 'H', text: 'Besides,' },
        { letter: 'J', text: 'In fact,' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: '"In fact" works best because the second sentence provides supporting evidence for the claim in the first sentence about neutrinos not being affected by matter.'
    },
    {
      id: 'english-25MC4-29',
      formId: 'english-25MC4',
      questionNumber: 29,
      passageId: 'passage-4',
      category: 'POW',
      isScored: true,
      question: 'On some occasions, however, neutrinos do collide with other particles. [29]',
      questionPrompt: 'At this point, the writer is considering adding the following true sentence: "In 1956, during the Cowan-Reines neutrino experiment, a neutrino was detected for the first time." Should the writer make this addition?',
      choices: [
        { letter: 'A', text: 'Yes, because the information is relevant to the history of neutrino detection outlined in the paragraph.' },
        { letter: 'B', text: 'Yes, because the information indicates that sub-zero altitude is essential to the detection of neutrinos.' },
        { letter: 'C', text: 'No, because the information is unrelated to the discussion of why scientists selected the location of the IceCube Neutrino Observatory.' },
        { letter: 'D', text: 'No, because the information is unrelated to why the detection of neutrinos is facilitated by zero-gravity conditions.' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: 'The paragraph discusses the IceCube Observatory\'s location and why Antarctica was chosen. The 1956 experiment, while historically interesting, doesn\'t relate to site selection.'
    },
    {
      id: 'english-25MC4-30',
      formId: 'english-25MC4',
      questionNumber: 30,
      passageId: 'passage-4',
      category: 'CSE',
      isScored: true,
      question: 'Scientists specifically selected the site of the IceCube Neutrino Observatory, and to facilitate the detection of such a collision.',
      underlinedText: 'Observatory, and to',
      questionPrompt: 'Which choice makes the sentence or sentences most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'Observatory, and to' },
        { letter: 'H', text: 'Observatory. To' },
        { letter: 'J', text: 'Observatory; to' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: 'The original is incorrect because "and to" creates a fragment. Looking at the options, F maintains the sentence but remains awkward—however, reviewing the passage context, the original structure may be correct.'
    },
    {
      id: 'english-25MC4-31',
      formId: 'english-25MC4',
      questionNumber: 31,
      passageId: 'passage-4',
      category: 'CSE',
      isScored: true,
      question: 'These factors increase the chance of DOMs detecting the blue flash that signifies a neutrino collision. Once this detection occurs, data are gathered and transferred to laboratories at the University of Wisconsin. Here, scientists determine the origin of each of these neutrinos by analyzing the direction and intensity of their flash.',
      underlinedText: 'their',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'their' },
        { letter: 'C', text: 'it\'s' },
        { letter: 'D', text: 'its\'' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: '"Their" correctly refers to "neutrinos" (plural). The pronoun agrees in number with its antecedent.'
    },
    {
      id: 'english-25MC4-32',
      formId: 'english-25MC4',
      questionNumber: 32,
      passageId: 'passage-4',
      category: 'POW',
      isScored: true,
      question: 'Determining neutrinos\' origins could provide scientists with new insights into the universe. For instance, some neutrinos are produced during supernovae (the collapsing of stars).',
      underlinedText: 'Determining neutrinos\' origins could provide scientists with new insights into the universe. For instance,',
      questionPrompt: 'Which of the following true sentences best introduces the main idea of the paragraph?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'For decades, scientists have been trying to learn more about gamma rays through the study of supernovae.' },
        { letter: 'H', text: 'Recently, scientists at IceCube discovered two neutrinos, which they now refer to as Bert and Ernie.' },
        { letter: 'J', text: 'Neutrinos can now be created in laboratories using a particle accelerator called a Super Proton Synchrotron.' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: 'The original best introduces the paragraph, which discusses how tracing neutrino origins could provide new scientific insights (such as information about supernovae).'
    },
    {
      id: 'english-25MC4-33',
      formId: 'english-25MC4',
      questionNumber: 33,
      passageId: 'passage-4',
      category: 'KLA',
      isScored: true,
      question: 'The origins of these neutrinos could give us opulent information about how, when, and why stars collapse.',
      underlinedText: 'opulent',
      questionPrompt: 'Which choice is clearest and most precise in context?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'invaluable' },
        { letter: 'C', text: 'upscale' },
        { letter: 'D', text: 'lavish' }
      ],
      correctIndex: 1,
      correctLetter: 'B',
      explanation: '"Invaluable" (extremely valuable) is the appropriate word for scientific information. "Opulent," "upscale," and "lavish" relate to wealth or luxury, not information.'
    },
    {
      id: 'english-25MC4-34',
      formId: 'english-25MC4',
      questionNumber: 34,
      passageId: 'passage-4',
      category: 'POW',
      isScored: true,
      question: 'Scientists are optimistic that the neutrinos detected at IceCube could lead to new ways of looking at our galaxy—and galaxies beyond.',
      underlinedText: 'and galaxies beyond',
      questionPrompt: 'The writer wants to emphasize that information garnered from the neutrinos detected at IceCube could have dramatic effects on how scientists study the universe. Which choice best accomplishes that goal?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'phenomena that have puzzled scientists over the last decade.' },
        { letter: 'H', text: 'common occurrences in space.' },
        { letter: 'J', text: 'the world around us.' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: '"And galaxies beyond" emphasizes the far-reaching scope of potential discoveries—from our own galaxy to distant ones—best conveying the dramatic effects on studying the universe.'
    },
    {
      id: 'english-25MC4-35',
      formId: 'english-25MC4',
      questionNumber: 35,
      passageId: 'passage-4',
      category: 'POW',
      isScored: true,
      question: 'Question 35 asks about the preceding passage as a whole.',
      questionPrompt: 'Suppose the writer\'s primary purpose had been to outline a scientific theory concerning the origins of a particle found in nature. Would this essay accomplish that goal?',
      choices: [
        { letter: 'A', text: 'Yes, because it explains how scientists are discovering new reasons why neutrinos emit a blue flash known as Cherenkov radiation.' },
        { letter: 'B', text: 'Yes, because it summarizes how DOMs at the IceCube Neutrino Observatory track neutrinos to their origins despite neutrinos\' numerous collisions with matter and electromagnetic forces.' },
        { letter: 'C', text: 'No, because it describes instead how neutrinos are detected at an observatory and how these detections could benefit future scientific research.' },
        { letter: 'D', text: 'No, because it details instead how new research on neutrinos could potentially contradict a commonly held theory about supernovae.' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: 'The essay describes the IceCube Observatory and how it detects neutrinos, plus the potential scientific benefits. It doesn\'t outline a theory about particle origins.'
    },

    // PASSAGE V: Clinton Hill's Found Artist (Q36-45)
    {
      id: 'english-25MC4-36',
      formId: 'english-25MC4',
      questionNumber: 36,
      passageId: 'passage-5',
      category: 'CSE',
      isScored: true,
      question: 'On the New York Times home page, I noticed an article about Rafael Leonardo Black, a 64-year-old Clinton Hill artist who had just been discovered.',
      underlinedText: 'who',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'of whom' },
        { letter: 'H', text: 'which' },
        { letter: 'J', text: 'whom' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: '"Who" is correct because it is the subject of the verb "had been discovered." The relative pronoun refers to "artist" and performs the action of being discovered.'
    },
    {
      id: 'english-25MC4-37',
      formId: 'english-25MC4',
      questionNumber: 37,
      passageId: 'passage-5',
      category: 'KLA',
      isScored: true,
      question: 'Black, a native of Aruba, has been creating art in his New York City studio apartment for over three decades.',
      underlinedText: 'a native of Aruba, has been creating art',
      questionPrompt: 'Which choice is least redundant in context?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'originally from Aruba, for more than half his life' },
        { letter: 'C', text: 'living in Clinton Hill but a native of Aruba,' },
        { letter: 'D', text: 'a newly found artist originally from Aruba,' }
      ],
      correctIndex: 0,
      correctLetter: 'A',
      explanation: 'The original is the most concise. The other options add redundant information (we already know he was "found" and lives in Clinton Hill from the previous paragraph).'
    },
    {
      id: 'english-25MC4-38',
      formId: 'english-25MC4',
      questionNumber: 38,
      passageId: 'passage-5',
      category: 'POW',
      isScored: true,
      question: 'However, in May of 2013, art dealer Francis Naumann, directed to Black\'s art by one of Black\'s longtime friends, displayed sixteen of the artist\'s drawings in a solo show.',
      underlinedText: 'displayed sixteen of the artist\'s drawings in a solo show',
      questionPrompt: 'Given that all the choices are accurate, which one provides the best transition to the information in the following sentence?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'was taking down an artist\'s long-running exhibition at his Manhattan gallery.' },
        { letter: 'H', text: 'became aware that Black had never shown his drawings, formally or otherwise.' },
        { letter: 'J', text: 'recognized that no one in the New York City art world had heard of Black.' }
      ],
      correctIndex: 0,
      correctLetter: 'F',
      explanation: 'The next sentence discusses pieces being sold, so the reference to displaying drawings in a solo show provides the best transition to information about sales.'
    },
    {
      id: 'english-25MC4-39',
      formId: 'english-25MC4',
      questionNumber: 39,
      passageId: 'passage-5',
      category: 'CSE',
      isScored: true,
      question: 'Within days, ten of Black\'s pieces sold for, prices ranging from $16,000 to $28,000.',
      underlinedText: 'sold for,',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'sold—for' },
        { letter: 'C', text: 'sold; for' },
        { letter: 'D', text: 'sold for:' }
      ],
      correctIndex: 1,
      correctLetter: 'B',
      explanation: 'The dash creates an effective pause before the dramatic price information. No comma should appear after "for."'
    },
    {
      id: 'english-25MC4-40',
      formId: 'english-25MC4',
      questionNumber: 40,
      passageId: 'passage-5',
      category: 'KLA',
      isScored: true,
      question: 'Black draws collages in black No. 2 pencil on white board. They\'re packed with depictions, in the form of drawings, of ancient myths, historical events, and popular culture.',
      underlinedText: 'with depictions, in the form of drawings,',
      questionPrompt: 'Which choice is least redundant in context?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'black pencil drawings that depict' },
        { letter: 'H', text: 'drawings that create collages of' },
        { letter: 'J', text: 'depictions of' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: 'We already know they are drawings from the previous sentence. "Depictions of" is sufficient and eliminates the redundancy of "in the form of drawings."'
    },
    {
      id: 'english-25MC4-41',
      formId: 'english-25MC4',
      questionNumber: 41,
      passageId: 'passage-5',
      category: 'CSE',
      isScored: false,
      question: 'It features a representation of a British psychedelic poster, a portrayal of Danish surrealist painter, Wilhelm Freddie, at work, and a tiny figure of Los Angeles architect Simon Rodia.',
      underlinedText: 'surrealist painter, Wilhelm Freddie,',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'painter Wilhelm Freddie,' },
        { letter: 'C', text: 'painter, Wilhelm Freddie' },
        { letter: 'D', text: 'painter Wilhelm Freddie' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: 'No commas are needed around "Wilhelm Freddie" because the name is essential to identify which surrealist painter is being referenced.'
    },
    {
      id: 'english-25MC4-42',
      formId: 'english-25MC4',
      questionNumber: 42,
      passageId: 'passage-5',
      category: 'POW',
      isScored: false,
      question: 'I wasn\'t sure how the drawings in Seven Lamps—so detailed that I could see the folds in Rodia\'s clothing—fit together logically, but I liked that there was so much for me to puzzle over.',
      underlinedText: 'but I liked that there was so much for me to puzzle over',
      questionPrompt: 'If the writer were to delete the underlined portion (adjusting the punctuation as needed), the essay would primarily lose a:',
      choices: [
        { letter: 'F', text: 'claim arguing that the reason Naumann chose to show Black\'s art is that the art offers so much for a viewer to reflect upon and analyze.' },
        { letter: 'G', text: 'detail indicating that the narrator appreciated Black\'s collage even though he or she might not have understood its overall intent.' },
        { letter: 'H', text: 'comment suggesting that though the narrator enjoys only some of Black\'s art, he or she is glad that Black has been discovered.' },
        { letter: 'J', text: 'statement revealing the narrator\'s belief that the best modern art is understood only by the artist who created it.' }
      ],
      correctIndex: 1,
      correctLetter: 'G',
      explanation: 'The underlined portion shows that despite not understanding how elements fit together, the narrator still enjoyed the complexity—appreciation without full comprehension.'
    },
    {
      id: 'english-25MC4-43',
      formId: 'english-25MC4',
      questionNumber: 43,
      passageId: 'passage-5',
      category: 'POW',
      isScored: false,
      question: 'Maybe this complexity in May helps explain why Black\'s work created such a stir.',
      underlinedText: 'in May',
      questionPrompt: 'The best placement for the underlined portion would be:',
      choices: [
        { letter: 'A', text: 'where it is now.' },
        { letter: 'B', text: 'after the word Maybe.' },
        { letter: 'C', text: 'after the word explain.' },
        { letter: 'D', text: 'after the word stir (and before the period).' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: '"In May" refers to when the stir occurred (the solo show), so it should be placed at the end: "why Black\'s work created such a stir in May."'
    },
    {
      id: 'english-25MC4-44',
      formId: 'english-25MC4',
      questionNumber: 44,
      passageId: 'passage-5',
      category: 'POW',
      isScored: false,
      question: 'I read that Black observes the sudden interest in his drawings. [D] He says he\'s always been an artist, regardless of who knew it.',
      underlinedText: 'observes',
      questionPrompt: 'The writer wants to clearly establish that the newspaper article claims Black is unmoved by the sudden interest in his art. Which choice accomplishes that goal?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'is nearly a celebrity in Clinton Hill due to' },
        { letter: 'H', text: 'has benefited financially from' },
        { letter: 'J', text: 'gives little thought to' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: '"Gives little thought to" indicates that Black is unmoved or indifferent to the sudden interest, which aligns with his statement that he\'s always been an artist regardless of recognition.'
    },
    {
      id: 'english-25MC4-45',
      formId: 'english-25MC4',
      questionNumber: 45,
      passageId: 'passage-5',
      category: 'POW',
      isScored: false,
      question: 'Question 45 asks about the preceding passage as a whole.',
      questionPrompt: 'The writer is considering adding the following sentence to the essay: "Fortunately, the web page included a key that identified the people, places, and events—most of which I had never even heard of—that Black portrays in this piece." If the writer were to add this sentence, it would most logically be placed at:',
      choices: [
        { letter: 'A', text: 'Point A in Paragraph 1.' },
        { letter: 'B', text: 'Point B in Paragraph 2.' },
        { letter: 'C', text: 'Point C in Paragraph 3.' },
        { letter: 'D', text: 'Point D in Paragraph 4.' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: 'The sentence refers to looking up a collage online and finding a key identifying elements. This fits in Paragraph 3 where the narrator describes finding Seven Lamps online and examining its contents.'
    },

    // PASSAGE VI: Cher Ami, Pigeon Hero (Q46-50)
    {
      id: 'english-25MC4-46',
      formId: 'english-25MC4',
      questionNumber: 46,
      passageId: 'passage-6',
      category: 'CSE',
      isScored: false,
      question: 'In many urban areas, pigeons are considered little more than, "rats with wings," blamed for spreading disease and despoiling statues.',
      underlinedText: 'more than,',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'than—' },
        { letter: 'H', text: 'than;' },
        { letter: 'J', text: 'than' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: 'No punctuation should appear between "than" and the quoted phrase that follows. The comma incorrectly separates the comparison.'
    },
    {
      id: 'english-25MC4-47',
      formId: 'english-25MC4',
      questionNumber: 47,
      passageId: 'passage-6',
      category: 'POW',
      isScored: false,
      question: 'For example, one species, the homing pigeon, is among the best navigators of the natural world.',
      underlinedText: 'For example,',
      questionPrompt: 'Which transition word or phrase is most logical in context?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'Similarly,' },
        { letter: 'C', text: 'However,' },
        { letter: 'D', text: 'Thus,' }
      ],
      correctIndex: 2,
      correctLetter: 'C',
      explanation: '"However" signals a contrast between the negative view of pigeons in the previous sentence and the positive qualities of homing pigeons that follow.'
    },
    {
      id: 'english-25MC4-48',
      formId: 'english-25MC4',
      questionNumber: 48,
      passageId: 'passage-6',
      category: 'CSE',
      isScored: false,
      question: 'There navigational ability has earned the homely pigeon an undeniable place in history.',
      underlinedText: 'There',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'They\'re' },
        { letter: 'H', text: 'It\'s' },
        { letter: 'J', text: 'Its' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: '"Its" (possessive) is needed to show that the navigational ability belongs to the homing pigeon species. "There" is a location word, and "They\'re" means "they are."'
    },
    {
      id: 'english-25MC4-49',
      formId: 'english-25MC4',
      questionNumber: 49,
      passageId: 'passage-6',
      category: 'CSE',
      isScored: false,
      question: 'One of six hundred birds used by the US Army Signal Corps in France during World War I, all twelve of Cher Ami\'s missions were deemed successful.',
      underlinedText: 'all twelve of Cher Ami\'s missions were deemed successful',
      questionPrompt: 'Which choice makes the sentence most grammatically acceptable?',
      choices: [
        { letter: 'A', text: 'No Change' },
        { letter: 'B', text: 'the twelve missions Cher Ami flew were successful.' },
        { letter: 'C', text: 'successful missions by Cher Ami numbered twelve.' },
        { letter: 'D', text: 'Cher Ami flew twelve successful missions.' }
      ],
      correctIndex: 3,
      correctLetter: 'D',
      explanation: 'The original has a dangling modifier—"One of six hundred birds" should modify Cher Ami, not "missions." Option D correctly makes Cher Ami the subject.'
    },
    {
      id: 'english-25MC4-50',
      formId: 'english-25MC4',
      questionNumber: 50,
      passageId: 'passage-6',
      category: 'KLA',
      isScored: false,
      question: 'The men were surrounded by German troops and were rapidly running out of rations. They were separated from other US forces.',
      underlinedText: 'They were separated from other US forces.',
      questionPrompt: 'Which choice is least redundant in context?',
      choices: [
        { letter: 'F', text: 'No Change' },
        { letter: 'G', text: 'German troops were all around them.' },
        { letter: 'H', text: 'They would soon be out of rations.' },
        { letter: 'J', text: 'Delete the underlined portion.' }
      ],
      correctIndex: 3,
      correctLetter: 'J',
      explanation: 'The information is redundant—"surrounded by German troops" already implies they were separated from US forces. Options G and H repeat information from the previous sentence.'
    }
  ]
};

// -----------------------------------------------------------------------------
// SCORING TABLE (40 scored questions → 1-36 scale)
// Source: Official ACT Prep Guide 2025-2026, Practice Test 4
// -----------------------------------------------------------------------------
export const scoringTable25MC4 = {
  40: 36, 39: 35, 38: 35, 37: 34, 36: 33, 35: 31, 34: 29, 33: 28,
  32: 26, 31: 25, 30: 24, 29: 23, 28: 23, 27: 22, 26: 21, 25: 20,
  24: 20, 23: 19, 22: 18, 21: 17, 20: 16, 19: 15, 18: 15, 17: 14,
  16: 14, 15: 13, 14: 12, 13: 11, 12: 11, 11: 10, 10: 10, 9: 10,
  8: 9, 7: 9, 6: 8, 5: 7, 4: 6, 3: 5, 2: 4, 1: 3, 0: 1
};

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------
export function getQuestion(questionNumber) {
  return form25MC4.questions.find(q => q.questionNumber === questionNumber);
}

export function getPassageQuestions(passageId) {
  return form25MC4.questions.filter(q => q.passageId === passageId);
}

export function getScoredQuestions() {
  return form25MC4.questions.filter(q => q.isScored);
}

export function getQuestionsByCategory(category) {
  return form25MC4.questions.filter(q => q.category === category);
}

export function calculateScaledScore(rawScore) {
  if (rawScore < 0) return 1;
  if (rawScore > 40) return 36;
  return scoringTable25MC4[rawScore] || 1;
}

// Default export
export default {
  ...form25MC4,
  scoringTable: scoringTable25MC4,
  getQuestion,
  getPassageQuestions,
  getScoredQuestions,
  getQuestionsByCategory,
  calculateScaledScore
};
